import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const UpdateSupplier = () => {
  const { sID } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [supplierData, setSupplierData] = useState({});
  const [selectedCity, setSelectedCity] = useState(null);
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      S_Name: supplierData.S_Name || "",
      S_Description: supplierData.S_Description || "",
      S_Type: supplierData.S_Type || "",
      S_Email: supplierData.S_Email || "",
      S_Contact: supplierData.S_Contact || "",
      S_Address1: supplierData.S_Address1 || "",
      S_Address2: supplierData.S_Address2 || "",
      Post_Code: supplierData.Post_Code || "",
      S_Country: supplierData.S_Country || "",
      S_City: selectedCity || "",
      S_Note: supplierData.S_Note || "",
      Is_Active: Boolean(supplierData.Is_Active),
    });
  }, [supplierData, selectedCity]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const fetchSupplierDetails = useCallback(async () => {
    if (!sID || !compID) {
      showErrorToast("Invalid supplier ID or company ID");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SUPPLIER_BY_ID, {
        params: { CompID: compID, S_ID: sID },
      });

      const data = response.data.data || response.data;

      const loaded = {
        ...data,
        S_ID: parseInt(sID),
        CompID: compID,
        S_DateTime: data.s_DateTime ? new Date(data.s_DateTime) : new Date(),
        S_Email: data.s_Email || "",
        S_Name: data.s_Name || "",
        S_Description: data.s_Description || "",
        S_Type: data.s_Type || "",
        S_Contact: data.s_Contact || "",
        S_Address1: data.s_Address1 || "",
        S_Address2: data.s_Address2 || "",
        Post_Code: data.post_Code || "",
        S_Country: data.s_Country || "",
        S_City: data.s_City ? String(data.s_City) : "",
        S_Note: data.s_Note || "",
        Is_Active: data.is_Active || true,
      };
      setSupplierData(loaded);
      const city = data.s_City ? String(data.s_City) : null;
      setSelectedCity(city);
      initialFormSnapshotRef.current = JSON.stringify({
        S_Name: loaded.S_Name || "",
        S_Description: loaded.S_Description || "",
        S_Type: loaded.S_Type || "",
        S_Email: loaded.S_Email || "",
        S_Contact: loaded.S_Contact || "",
        S_Address1: loaded.S_Address1 || "",
        S_Address2: loaded.S_Address2 || "",
        Post_Code: loaded.Post_Code || "",
        S_Country: loaded.S_Country || "",
        S_City: city || "",
        S_Note: loaded.S_Note || "",
        Is_Active: Boolean(loaded.Is_Active),
      });
    } catch (error) {
      showErrorToast("Failed to load supplier details");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [sID, compID]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setSupplierData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // Handle toggle switch changes
  const handleToggleChange = useCallback((checked, name) => {
    setSupplierData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const submitSupplier = async (submitData, exitAfterSave) => {
    try {
      setLoading(true);
      submitData.CompID = compID;
      submitData.S_ID = parseInt(sID);

      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_SUPPLIER,
        submitData,
      );

      if (response.data.status === true) {
        showSuccessToast(
          response.data.message || "Supplier Successfully Updated."
        );
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate("/supplier/manageSupplier");
        }
        return true;
      }
      showErrorToast(response.data.message || "Error Updating Supplier");
      return false;
    } catch (err) {
      if (err.response) {
        showErrorToast(
          err.response.data.message || "Error submitting supplier"
        );
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in supplier submission");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!supplierData.S_Name?.trim()) {
      showErrorToast("Please provide a valid supplier name");
      return false;
    }

    if (!supplierData.S_Email || !validateEmail(supplierData.S_Email)) {
      showErrorToast("Please provide a valid email");
      return false;
    }

    if (supplierData.S_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return false;
    }

    const submitData = {
      ...supplierData,
      S_DateTime: new Date().toISOString(),
      S_City: selectedCity || "",
    };

    return submitSupplier(submitData, exitAfterSave);
  };

  useEffect(() => {
    fetchSupplierDetails();
  }, [fetchSupplierDetails]);

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );
  
  return (
    <>
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/"><i className="fa fa-home me-1"></i>Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/supplier/manageSupplier"><i className="fa fa-building me-1"></i>Manage Suppliers</Link>
              </li>
              <li className="breadcrumb-item active"><i className="fa fa-edit me-1"></i>Update Supplier</li>
            </ol>
            <h1 className="page-header mb-0"><i className="fa fa-edit me-2 text-primary"></i>Update Supplier</h1>
          </div>
          <SaveActions
            onCancel={() => navigate("/supplier/manageSupplier")}
            onSave={() => handleSubmit({ exitAfterSave: false })}
            onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
            saving={loading}
          />
        </div>

        <GridSectionLoader loading={loading} minHeight="240px">
        <Panel className="card border-0 shadow-sm">
          <PanelHeader className="bg-primary text-white">Supplier Details</PanelHeader>
          <PanelBody className="p-4">
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <>
                {/* Supplier Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-building me-2"></i>Supplier Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <RequiredLabel text="Supplier Name" />
                    <FormInput
                      name="S_Name"
                      value={supplierData.S_Name}
                      onchange={handleInputChange}
                      placeholder="Enter Supplier Name"
                      prepend={<i className="fa fa-building text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-6">
                    <NormalLabel text="Supplier Type" />
                    <FormInput
                      name="S_Type"
                      value={supplierData.S_Type}
                      onchange={handleInputChange}
                      placeholder="Enter Supplier Type"
                      prepend={<i className="fa fa-tag text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-12">
                    <NormalLabel text="Description" />
                    <FormInput
                      name="S_Description"
                      value={supplierData.S_Description}
                      onchange={handleInputChange}
                      placeholder="Enter Description"
                      prepend={<i className="fa fa-info text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Contact Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-address-book me-2"></i>Contact Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <RequiredLabel text="Email" />
                    <FormInput
                      name="S_Email"
                      placeholder="Enter Email"
                      type="email"
                      value={supplierData.S_Email}
                      onchange={handleInputChange}
                      prepend={<i className="fa fa-envelope text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-6">
                    <NormalLabel text="Contact Number" />
                    <FormInput
                      name="S_Contact"
                      placeholder="Enter Contact Number"
                      type="text"
                      value={supplierData.S_Contact}
                      onchange={handleInputChange}
                      prepend={<i className="fa fa-phone text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Address Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-map-marker me-2"></i>Address Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <NormalLabel text="Address Line 1" />
                    <FormInput
                      name="S_Address1"
                      value={supplierData.S_Address1}
                      onchange={handleInputChange}
                      placeholder="Enter Address Line 1"
                      prepend={<i className="fa fa-road text-muted"></i>}
                    />
                  </div>
                  <div className="col-md-12">
                    <NormalLabel text="Address Line 2" />
                    <FormInput
                      name="S_Address2"
                      value={supplierData.S_Address2}
                      onchange={handleInputChange}
                      placeholder="Enter Address Line 2"
                      prepend={<i className="fa fa-road text-muted"></i>}
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <RequiredLabel text="Country / City" />
                  <CountryCitySelector
                    selectedCountry={supplierData.S_Country}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChange={(data) =>
                      setSupplierData((prev) => ({ ...prev, ...data }))
                    }
                    setLoading={setLoading}
                    isDarkMode={isDarkMode}
                    selectedValueIsLabel={true}
                    countryFieldName="S_Country"
                    cityFieldName="S_City"
                  />
                </div>
                <div className="row g-3 mt-2 mb-4">
                  <div className="col-md-6">
                    <NormalLabel text="Post Code" />
                    <FormInput
                      name="Post_Code"
                      value={supplierData.Post_Code}
                      onchange={handleInputChange}
                      placeholder="Enter Post Code"
                      prepend={<i className="fa fa-map-pin text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Additional Information */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-info-circle me-2"></i>Additional Information
                </h5>
                <div className="row g-3 mt-2 mb-4">
                  <div className="col-md-12">
                    <NormalLabel text="Notes" />
                    <FormInput
                      name="S_Note"
                      value={supplierData.S_Note}
                      onchange={handleInputChange}
                      placeholder="Enter Notes"
                      prepend={<i className="fa fa-sticky-note text-muted"></i>}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Status */}
                <h5 className="mb-4 text-primary fw-semibold">
                  <i className="fa fa-toggle-on me-2"></i>Status
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <ToggleSwitch
                      isChecked={supplierData.Is_Active || false}
                      onToggle={(checked) => handleToggleChange(checked, "Is_Active")}
                      name="Is_Active"
                      label="Active"
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </>
            )}
          </PanelBody>
        </Panel>
        </GridSectionLoader>
      </div>
    </>
  );
};

export default UpdateSupplier;